import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, HostBinding, Input, OnChanges, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'hf-custom-radio-button',
  templateUrl: './custom-radio-button.component.html',
  styleUrls: ['./custom-radio-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomRadioButtonComponent),
      multi: true
    }
  ]
})
export class CustomRadioButtonComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @ViewChild('radioInput', { static: false }) radioInput?: ElementRef<HTMLInputElement>;

  /** Group name */
  @Input() name?: string;

  /** The value the radio represents */
  @Input() value!: string | number | boolean;

  /** Optional label text */
  @Input() label = '';

  /** Optional description text */
  @Input() description = '';

  /** Disable state */
  @Input() disabled = false;

  /** Optional aria-describedby id(s) */
  @Input() ariaDescribedBy?: string;

  /** Optional outer layer style */
  @Input() outerLayer = false;

  // CVA plumbing
  private _modelValue: any;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.updateNativeInputChecked();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Trigger change detection when inputs change
    if (changes['value'] || changes['disabled']) {
      this.cdr.markForCheck();
      this.updateNativeInputChecked();
    }
  }

  private updateNativeInputChecked(): void {
    if (this.radioInput?.nativeElement) {
      this.radioInput.nativeElement.checked = this.checked;
    }
  }

  get checked(): boolean {
    // Handle both strict equality and type-coerced equality for boolean values
    if (typeof this.value === 'boolean' && typeof this._modelValue === 'boolean') {
      return this._modelValue === this.value;
    }
    // Use loose equality for other types to handle string/number conversions
    return this._modelValue == this.value;
  }

  @HostBinding('class.disabled') get isDisabled() {
    return this.disabled;
  }

  writeValue(obj: any): void {
    this._modelValue = obj;
    this.cdr.markForCheck();
    this.updateNativeInputChecked();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public handleChange() {
    if (this.disabled) return;
    this._modelValue = this.value;
    this.onChange(this.value);
    this.onTouched();
    this.cdr.markForCheck();
    this.updateNativeInputChecked();
  }

  public onChange: (v: any) => void = () => {};
  public onTouched: () => void = () => {};
}
